/**
 * Load more for paginated product grids. Wraps a plain link to the next page (which still works without JS),
 * fetches that page, appends its grid items to the current grid, and points the link at the following page.
 * Attributes: grid="<id of the list to append to>", data-loading-label, data-count-template ("{shown}" / "{total}").
 *
 * Return-to-place: when a shopper taps a product in the grid, the number of extra pages loaded and the tapped
 * link are kept in sessionStorage for this URL. Coming back with the browser Back button reloads those pages
 * and scrolls the tapped product into view, so shoppers never restart from the top.
 */
const RESTORE_MAX_AGE = 30 * 60 * 1000;

class SLoadMore extends HTMLElement {
  #controller = null;
  #pagesLoaded = 0;

  connectedCallback() {
    this.link = this.querySelector('a[href]');
    this.grid = document.getElementById(this.getAttribute('grid'));
    this.storageKey = `lachi-browse:${window.location.pathname}${window.location.search}`;
    this.onGridClick = (event) => {
      const target = event.target.closest('a[href]');
      if (!target || !this.grid?.contains(target)) return;
      this.#remember(target.getAttribute('href'));
    };
    this.grid?.addEventListener('click', this.onGridClick, true);

    if (this.link) {
      this.label = this.link.innerHTML;
      this.onClick = (event) => {
        event.preventDefault();
        this.#load();
      };
      this.link.addEventListener('click', this.onClick);
    }
    this.#restore();
  }

  disconnectedCallback() {
    this.link?.removeEventListener('click', this.onClick);
    this.grid?.removeEventListener('click', this.onGridClick, true);
    this.#controller?.abort();
  }

  #remember(href) {
    try {
      sessionStorage.setItem(
        this.storageKey,
        JSON.stringify({ pages: this.#pagesLoaded, href, y: window.scrollY, t: Date.now() })
      );
    } catch (error) {
      /* Storage can be unavailable (private mode); returning still works, just from the top. */
    }
  }

  async #restore() {
    let state = null;
    try {
      state = JSON.parse(sessionStorage.getItem(this.storageKey) || 'null');
    } catch (error) {
      return;
    }
    if (!state) return;
    const navigation = performance.getEntriesByType?.('navigation')[0];
    const cameBack = navigation?.type === 'back_forward';
    if (!cameBack || Date.now() - state.t > RESTORE_MAX_AGE) {
      sessionStorage.removeItem(this.storageKey);
      return;
    }
    if ('scrollRestoration' in history) history.scrollRestoration = 'manual';
    for (let page = 0; page < state.pages && this.link?.isConnected; page += 1) {
      const loaded = await this.#load({ restoring: true });
      if (!loaded) break;
    }
    const match = state.href
      ? [...(this.grid?.querySelectorAll('a[href]') || [])].find((anchor) => anchor.getAttribute('href') === state.href)
      : null;
    const card = match?.closest('li');
    if (card) {
      card.scrollIntoView({ block: 'center' });
    } else {
      window.scrollTo(0, state.y || 0);
    }
  }

  async #load({ restoring = false } = {}) {
    if (this.hasAttribute('loading') || !this.link) return false;
    const grid = this.grid;
    if (!grid) {
      if (!restoring) window.location.href = this.link.href;
      return false;
    }
    this.setAttribute('loading', '');
    this.link.setAttribute('aria-disabled', 'true');
    this.link.textContent = this.dataset.loadingLabel || this.link.textContent;
    this.#controller = new AbortController();

    try {
      const response = await fetch(this.link.href, { signal: this.#controller.signal });
      if (!response.ok) throw new Error(String(response.status));
      const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
      const nextGrid = doc.getElementById(grid.id);
      const items = nextGrid ? [...nextGrid.children] : [];
      const firstNew = items[0];
      items.forEach((item) => grid.append(document.adoptNode(item)));
      this.#pagesLoaded += 1;

      const nextLoader = doc.querySelector(this.localName);
      const nextLink = nextLoader?.querySelector('a[href]');
      const count = this.querySelector('[data-count]');
      const nextCount = nextLoader?.querySelector('[data-count]');
      if (count && nextCount) count.textContent = nextCount.textContent;

      if (nextLink) {
        this.link.href = nextLink.href;
        this.link.innerHTML = this.label;
        this.link.removeAttribute('aria-disabled');
      } else {
        this.link.remove();
        this.link = null;
      }

      if (!restoring) {
        const focusTarget = firstNew?.querySelector('a[href]');
        focusTarget?.focus({ preventScroll: true });
      }
      return true;
    } catch (error) {
      if (error.name === 'AbortError') return false;
      if (!restoring && this.link) window.location.href = this.link.href;
      return false;
    } finally {
      this.removeAttribute('loading');
    }
  }
}

if (!customElements.get('s-load-more')) customElements.define('s-load-more', SLoadMore);
